import { useEffect, useState } from 'react';
import { D, formatMoney } from '@makila/fiscal';
import { Package, Pencil, Plus, Trash2 } from 'lucide-react';
import { api, patch, post } from './api';
import { useBusiness } from './business';
import { Alert, Badge, Button, Card, EmptyState, Field, FormActions, Input, Select, Skeleton, useToast } from './ui';
import { attempt, useLoad } from './useLoad';
import { ManagedByMakila, useMakilaLinked } from './makila';
import { type Category, draftOf, margin, newDraft, type Product, ProductForm, type ProductDraft, type Reference, saveProduct } from './ProductEditor';

type Filter = 'all' | 'product' | 'service' | 'kit' | 'incomplete' | 'inactive';
const FILTERS: { value: Filter; label: string; test: (p: Product) => boolean }[] = [
  { value: 'all', label: 'Todos', test: () => true },
  { value: 'product', label: 'Produtos', test: (p) => p.kind === 'product' || p.kind === 'container' },
  { value: 'service', label: 'Serviços', test: (p) => p.kind === 'service' },
  { value: 'kit', label: 'Kits', test: (p) => p.kind === 'kit' },
  { value: 'incomplete', label: 'Ficha incompleta', test: (p) => !p.image || !p.cost_price },
  { value: 'inactive', label: 'Inactivos', test: (p) => !p.active },
];

/**
 * Artigos (docs/14 §5): lista com miniatura, margem e filtros; a ficha abre
 * na própria página, com as secções dos módulos activos.
 */
export function ProductsPage() {
  const products = useLoad<{ items: Product[] }>('/products');
  const cats = useLoad<{ items: Category[] }>('/categories');
  const ref = useLoad<Reference>('/reference');
  const { has, type } = useBusiness();
  const toast = useToast();
  const [edit, setEdit] = useState<{ id: string | null; f: ProductDraft; saved: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const linked = useMakilaLinked();

  if (products.error) return <Alert>{products.error}</Alert>;
  if (!products.data || !cats.data || !ref.data) return <Card><Skeleton rows={6} /></Card>;
  const catOf = new Map(cats.data.items.map((c) => [c.id, c]));
  const all = products.data.items;
  const s = q.trim().toLowerCase();
  const list = all.filter((p) => FILTERS.find((f) => f.value === filter)!.test(p)
    && (!s || [p.name, p.code, p.barcode, p.brand, p.supplier, p.supplier_code, ...p.extra_barcodes, ...p.tags].some((x) => x?.toLowerCase().includes(s))));
  const filters = FILTERS.filter((f) => f.value !== 'kit' || has('recipes')).map((f) => ({ ...f, n: all.filter(f.test).length })).filter((f) => f.value === 'all' || f.n > 0 || f.value === filter);
  const dirty = edit ? JSON.stringify(edit.f) !== edit.saved : false;
  const open = (id: string | null, f: ProductDraft) => { setErr(null); setEdit({ id, f, saved: JSON.stringify(f) }); scrollTo({ top: 0 }); };
  const close = () => {
    // Sair com alterações por gravar pede confirmação (docs/14 §4.3).
    if (dirty && !confirm('Há alterações por gravar. Sair sem gravar?')) return;
    setEdit(null); setErr(null);
  };

  const save = async () => {
    if (!edit) return;
    setBusy(true);
    const r = await saveProduct(edit.id, edit.f, linked, has);
    setBusy(false);
    setErr(r.error);
    if (!r.error) { toast(edit.id ? 'Artigo gravado' : 'Artigo criado'); setEdit(null); void products.reload(); }
    else if (r.id && !edit.id) setEdit({ ...edit, id: r.id }); // criado, mas um passo seguinte falhou: continua na mesma ficha
  };

  // A ficha ocupa a página: menos distracções e espaço para todas as secções.
  if (edit) {
    const current = edit.id ? all.find((p) => p.id === edit.id) : undefined;
    return (
      <div className="max-w-6xl space-y-6">
        <Card title={edit.id ? edit.f.name || 'Editar artigo' : `Novo ${type.terms.item.toLowerCase()}`} description={edit.id ? `Código ${edit.f.code}` : undefined}>
          <ProductForm id={edit.id} f={edit.f} set={(f) => setEdit({ ...edit, f })} cats={cats.data.items} ref_={ref.data} linked={linked} all={all} onReload={() => void products.reload()} />
          {err && <div className="mt-6"><Alert>{err}</Alert></div>}
          <FormActions>
            <Button onClick={save} disabled={busy}>{busy ? 'A gravar…' : 'Gravar'}</Button>
            <Button variant="ghost" onClick={close}>Cancelar</Button>
          </FormActions>
        </Card>
        {current && has('recipes') && <RecipeEditor key={current.id} p={current} all={all} kit={edit.f.kind === 'kit'} />}
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      {linked ? <ManagedByMakila what="artigos" /> : <Alert tone="info">As alterações chegam às caixas na sincronização seguinte (cerca de 20 segundos com internet).</Alert>}
      {(has('scale') || has('loyalty')) && <ScaleSettings />}
      <Card title={`${all.length} ${all.length === 1 ? type.terms.item.toLowerCase() : type.terms.items.toLowerCase()}`} actions={
        <div className="flex flex-wrap gap-2">
          <Input aria-label="Pesquisar artigos" placeholder="Nome, código, marca, etiqueta…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" />
          {!linked && <Button icon={Plus} onClick={() => open(null, newDraft({ kind: type.terms.item === 'Serviço' ? 'service' : 'product', unit: type.units[0] ?? 'un' }))}>Novo</Button>}
        </div>
      } padded={false}>
        <div className="flex flex-wrap gap-1.5 px-4 pt-4 xl:px-6">
          {filters.map((f) => (
            <button key={f.value} type="button" onClick={() => setFilter(f.value)} aria-pressed={filter === f.value}
              className={`h-9 rounded-full px-3.5 text-sm font-medium transition ${filter === f.value ? 'bg-ink text-white dark:bg-white dark:text-ink' : 'bg-surface-2 text-muted hover:text-[var(--text)]'}`}>
              {f.label} <span className="num opacity-70">{f.n}</span>
            </button>
          ))}
        </div>
        {!all.length ? (
          <EmptyState icon={Package} title={`Ainda não há ${type.terms.items.toLowerCase()}`} text="Crie o primeiro: aparece nas caixas na sincronização seguinte."
            action={!linked && <Button icon={Plus} onClick={() => open(null, newDraft())}>Novo</Button>} />
        ) : (
          <div className="overflow-x-auto p-2 xl:p-3">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted">
                <tr><th className="w-16 px-2 py-2" /><th className="px-2">Nome</th><th className="hidden px-2 md:table-cell">Categoria</th><th className="px-2 text-right">Preço</th><th className="hidden px-2 text-right sm:table-cell">Margem</th><th className="hidden px-2 text-right lg:table-cell">IVA</th><th className="w-12" /></tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {list.map((p) => {
                  const c = p.category_id ? catOf.get(p.category_id) : undefined;
                  const m = p.cost_price ? margin(p.unit_price, p.price_includes_tax, p.tax_rate, p.cost_price) : null;
                  return (
                    <tr key={p.id} onClick={() => open(p.id, draftOf(p))} className={`cursor-pointer hover:bg-surface-2 ${p.active ? '' : 'opacity-50'}`}>
                      <td className="px-2 py-2">
                        {p.image
                          ? <img src={p.image} alt="" className="h-12 w-12 rounded-xl object-cover" />
                          : <span className="grid h-12 w-12 place-items-center rounded-xl text-xs font-bold text-white" style={{ background: p.button_color ?? c?.color ?? '#9aa8bc' }}>{p.name.slice(0, 2).toUpperCase()}</span>}
                      </td>
                      <td className="px-2">
                        <div className="flex flex-wrap items-center gap-1.5 font-medium">
                          {p.name}
                          {p.kind === 'service' && <Badge tone="info">serviço{p.duration_min ? ` · ${p.duration_min} min` : ''}</Badge>}
                          {p.kind === 'kit' && <Badge tone="info">kit</Badge>}
                          {p.kind === 'container' && <Badge tone="info">vasilhame</Badge>}
                          {p.age_restricted && <Badge tone="warn">18+</Badge>}
                          {!p.pos_visible && <Badge>fora da caixa</Badge>}
                          {!p.active && <Badge>inactivo</Badge>}
                        </div>
                        <div className="mt-0.5 text-xs text-muted">
                          <span className="font-mono">{p.code}</span>
                          {[p.brand, p.barcode, p.sold_by_weight && 'ao peso', p.price_tiers.length > 0 && `${p.price_tiers.length} preço(s) por quantidade`, p.variant_of && `variante: ${p.variant_label}`, p.tags.length > 0 && p.tags.map((t) => `#${t}`).join(' ')].filter(Boolean).map((x) => ` · ${x}`).join('')}
                        </div>
                      </td>
                      <td className="hidden px-2 md:table-cell">{c?.name ?? '—'}</td>
                      <td className="num whitespace-nowrap px-2 text-right">{p.open_price ? <span className="text-muted">na caixa</span> : <>{formatMoney(p.unit_price)}{p.price_includes_tax ? '' : ' + IVA'}</>}</td>
                      <td className={`num hidden px-2 text-right sm:table-cell ${m?.pct.isNegative() ? 'text-brand-700' : ''}`}>{m ? `${m.pct.toFixed(0)}%` : '—'}</td>
                      <td className="num hidden whitespace-nowrap px-2 text-right lg:table-cell">{new D(p.tax_rate).toString()}%{p.exemption_code ? ` ${p.exemption_code}` : ''}</td>
                      <td className="px-2 text-right">
                        <button aria-label={`Editar ${p.name}`} onClick={(e) => { e.stopPropagation(); open(p.id, draftOf(p)); }} className="inline-grid h-10 w-10 place-items-center rounded-lg text-muted hover:bg-[var(--surface)] hover:text-[var(--text)]"><Pencil size={16} /></button>
                      </td>
                    </tr>
                  );
                })}
                {!list.length && <tr><td colSpan={7} className="py-10 text-center text-muted">Nenhum artigo com esse filtro.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export function CategoriesPage() {
  const { data, error, reload } = useLoad<{ items: Category[] }>('/categories');
  const [f, setF] = useState({ name: '', color: '#a6070c' });
  const [err, setErr] = useState<string | null>(null);
  const linked = useMakilaLinked();
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <p className="text-muted">A carregar…</p>;
  return (
    <div className="max-w-3xl space-y-4">
      {err && <Alert>{err}</Alert>}
      {linked && <ManagedByMakila what="artigos e as suas categorias" />}
      {!linked && <Card title="Nova categoria">
        <form className="flex flex-wrap items-end gap-3" onSubmit={async (e) => {
          e.preventDefault();
          const x = await attempt(() => post('/categories', { name: f.name, color: f.color, sortOrder: data.items.length + 1 }));
          setErr(x);
          if (!x) { setF({ ...f, name: '' }); void reload(); }
        }}>
          <Field label="Nome"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Cor"><input type="color" aria-label="Cor" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="h-11 w-16 rounded-lg border border-base" /></Field>
          <Button><Plus size={16} /> Criar</Button>
        </form>
      </Card>}
      <Card title="Categorias">
        <ul className="divide-y divide-[var(--border)]">
          {data.items.map((c) => (
            <li key={c.id} className="flex items-center gap-3 py-2">
              <span className="h-5 w-5 rounded-full border border-base" style={{ background: c.color ?? 'transparent' }} />
              <span className={`flex-1 ${c.active ? '' : 'opacity-50'}`}>{c.name}</span>
              {!linked && <Button variant="ghost" onClick={async () => { setErr(await attempt(() => patch(`/categories/${c.id}`, { active: !c.active }))); void reload(); }}>{c.active ? 'Desactivar' : 'Activar'}</Button>}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

/** O que a balança põe nas etiquetas (como ela estiver configurada). */
function ScaleSettings() {
  const { data, reload } = useLoad<{ scaleBarcodeValue: 'weight' | 'price'; loyaltyEnabled: boolean }>('/settings/retail');
  const [err, setErr] = useState<string | null>(null);
  if (!data) return null;
  return (
    <Card title="Balança e pontos">
      <Field label="Nas etiquetas com código começado por 2, o valor é" hint="Como a balança imprime: o peso (gramas) ou o preço a pagar (centavos). Chega às caixas na sincronização seguinte.">
        <Select aria-label="Valor nas etiquetas da balança" value={data.scaleBarcodeValue} onChange={async (e) => { setErr(await attempt(() => patch('/settings/retail', { scaleBarcodeValue: e.target.value }))); void reload(); }}>
          <option value="weight">O peso</option><option value="price">O preço</option>
        </Select>
      </Field>
      <label className="mt-4 flex items-center gap-2">
        <input type="checkbox" aria-label="Programa de pontos" checked={data.loyaltyEnabled} onChange={async (e) => { setErr(await attempt(() => patch('/settings/retail', { loyaltyEnabled: e.target.checked }))); void reload(); }} />
        Programa de pontos: 1 ponto por cada 100 MT (clientes identificados); 1 ponto = 1 MT, até 50% da compra; expiram em 12 meses
      </label>
      {err && <div className="mt-2"><Alert>{err}</Alert></div>}
    </Card>
  );
}

/** Retalho de um artigo: venda ao peso, código da balança, preços por quantidade, variante de um modelo. */
function RecipeEditor({ p, all, kit }: { p: Product; all: Product[]; kit: boolean }) {
  const [lines, setLines] = useState<{ ingredientId: string; quantity: string }[] | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => {
    void api<{ lines: { ingredient_id: string; quantity: string }[] }>(`/products/${p.id}/recipe`).catch(() => ({ lines: [] })).then((r) => setLines(r.lines.map((l) => ({ ingredientId: l.ingredient_id, quantity: new D(l.quantity).toString().replace('.', ',') }))));
  }, [p.id]);
  if (!lines) return null;
  const options = all.filter((x) => x.id !== p.id && x.active);
  const save = async () => {
    const e = await attempt(() => api(`/products/${p.id}/recipe`, { method: 'PUT', body: JSON.stringify({ lines: lines.filter((l) => l.ingredientId) }) }));
    setMsg(e ? { tone: 'error', text: e } : { tone: 'ok', text: 'Ficha técnica gravada.' });
  };
  return (
    <Card title={kit ? 'Componentes do kit' : 'Ficha técnica'} description={kit ? 'O que sai do stock por cada kit vendido.' : 'Cada unidade vendida abate estes ingredientes do stock da loja (em vez do próprio artigo). Vazia: o artigo abate-se a si próprio.'}>
      {lines.map((l, i) => (
        <div key={i} className="mt-2 flex items-center gap-2">
          <Select aria-label="Ingrediente" value={l.ingredientId} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, ingredientId: e.target.value } : x)))}>
            <option value="">— ingrediente —</option>
            {options.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.unit})</option>)}
          </Select>
          <Input aria-label="Quantidade do ingrediente" inputMode="decimal" className="w-28" value={l.quantity} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} />
          <button aria-label="Retirar ingrediente" onClick={() => setLines(lines.filter((_, j) => j !== i))} className="grid h-10 w-10 place-items-center rounded-lg text-brand-700"><Trash2 size={16} /></button>
        </div>
      ))}
      <div className="mt-3 flex gap-2">
        <Button variant="ghost" onClick={() => setLines([...lines, { ingredientId: '', quantity: '1' }])}><Plus size={16} /> Ingrediente</Button>
        <Button onClick={save}>Gravar ficha</Button>
      </div>
      {msg && <div className="mt-3"><Alert tone={msg.tone}>{msg.text}</Alert></div>}
    </Card>
  );
}
